'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { useIsClient } from '@/hooks';

/**
 * Props for `BenefitsListWithIcons`.
 */
export type BenefitsListWithIconsProps =
  SliceComponentProps<Content.BenefitsListWithIconsSlice>;

/**
 * Component for "BenefitsListWithIcons" Slices.
 * A list of benefits with icons, titles, and descriptions with alternating backgrounds.
 */
const BenefitsListWithIcons: FC<BenefitsListWithIconsProps> = ({ slice }) => {
  const { main_title, intro_text, items } = slice.primary;

  const headerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const headerEl = headerRef.current;
    const listEl = listRef.current;

    if (headerEl) {
      headerEl.style.transitionDelay = '0ms';
      observer.observe(headerEl);
    }

    if (listEl) {
      listEl.style.transitionDelay = '200ms';
      observer.observe(listEl);
    }

    return () => {
      if (headerEl) {
        observer.unobserve(headerEl);
      }
      if (listEl) {
        observer.unobserve(listEl);
      }
    };
  }, [isClient]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white pt-[94px] pb-[50px] px-4 md:px-8 lg:px-[200px]"
    >
      <div className="max-w-[1040px] mx-auto flex flex-col gap-6 items-start justify-center">
        {/* Header Section */}
        <div
          ref={headerRef}
          className="flex flex-col gap-4 w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {/* Main Title */}
          {isFilled.richText(main_title) && (
            <PrismicRichText
              field={main_title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-[#041018]">
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {/* Intro Text */}
          {isFilled.richText(intro_text) && (
            <PrismicRichText
              field={intro_text}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-lg font-normal leading-[1.4] text-[#041018]">
                    {children}
                  </p>
                ),
              }}
            />
          )}
        </div>

        {/* Benefits List */}
        {items && items.length > 0 && (
          <div
            ref={listRef}
            className="flex flex-col gap-2 w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {items.map((item, index) => {
              // Alternate between white and light green backgrounds
              const isEven = index % 2 === 0;
              const bgColor = isEven ? 'bg-white' : 'bg-[#f4fcf7]';
              const borderStyle = isEven
                ? 'border border-[#dedee9]'
                : 'border border-white';

              return (
                <div
                  key={index}
                  className={`${bgColor} ${borderStyle} rounded-xl p-4 shadow-[0px_4px_12px_0px_rgba(13,10,44,0.06)] flex flex-col gap-2`}
                >
                  {/* Icon + Headline Row */}
                  <div className="flex gap-2 items-center">
                    {/* Icon */}
                    {isFilled.image(item.icon) && (
                      <div className="w-12 h-12 shrink-0">
                        <PrismicNextImage
                          field={item.icon}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}

                    {/* Headline */}
                    {item.headline && (
                      <h3 className="flex-1 text-xl font-semibold leading-[1.4] text-[#041018]">
                        {item.headline}
                      </h3>
                    )}
                  </div>

                  {/* Description */}
                  {item.description && (
                    <p className="text-lg font-normal leading-[1.4] text-[#041018]">
                      {item.description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default BenefitsListWithIcons;
